import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

const sixty = [
  { label: "Single Session", price: 150, sessions: 1 },
  { label: "3-Pack", price: 427, sessions: 3 },
  { label: "4-Pack", price: 560, sessions: 4 },
  { label: "6-Pack", price: 810, sessions: 6 },
  { label: "9-Pack", price: 1181, sessions: 9 },
  { label: "12-Pack", price: 1530, sessions: 12 },
  { label: "15-Pack", price: 1856, sessions: 15 },
  { label: "18-Pack", price: 2160, sessions: 18 },
];

const ninety = [
  { label: "Single Session", price: 165, sessions: 1 },
  { label: "3-Pack", price: 470, sessions: 3 },
  { label: "4-Pack", price: 594, sessions: 4 },
  { label: "6-Pack", price: 891, sessions: 6 },
  { label: "9-Pack", price: 1300, sessions: 9 },
  { label: "12-Pack", price: 1683, sessions: 12 },
  { label: "15-Pack", price: 2042, sessions: 15 },
  { label: "18-Pack", price: 2376, sessions: 18 },
];

const perSession = (p) => (p.sessions > 1 ? Math.round(p.price / p.sessions) : null);

const Tier = ({ title, subtitle, single, packages, accent, testidPrefix }) => (
  <div
    data-testid={`${testidPrefix}-tier`}
    className="bg-[#FDFCFB] border border-[#E8E5DF] rounded-md p-8 md:p-10"
  >
    <div className="flex items-baseline justify-between mb-2">
      <h3 className="font-serif text-3xl text-[#2D3A2B]">{title}</h3>
      <span
        className="text-xs uppercase tracking-widest px-3 py-1 rounded-full"
        style={{ backgroundColor: accent, color: "#2D3A2B" }}
      >
        Sessions
      </span>
    </div>
    <p className="text-sm text-[#4A5D46] mb-6 font-light">{subtitle}</p>
    <p className="font-serif text-5xl text-[#2D3A2B] mb-8">
      ${single}
      <span className="text-base text-[#7B8E76] ml-2 font-sans">/ single session</span>
    </p>
    <div className="border-t border-[#E8E5DF] pt-6 space-y-3">
      {packages.map((p, i) => (
        <div
          key={p.label}
          data-testid={`${testidPrefix}-row-${i}`}
          className="flex items-center justify-between py-2 border-b border-[#F4F1ED] last:border-b-0"
        >
          <div className="flex items-center gap-3">
            <Check className="w-4 h-4 text-[#8A9A83]" aria-hidden />
            <span className="text-[#2D3A2B]">{p.label}</span>
          </div>
          <div className="text-right">
            <span className="font-serif text-xl text-[#2D3A2B]">
              ${p.price.toLocaleString()}
            </span>
            {perSession(p) && (
              <span className="block text-xs text-[#7B8E76]">
                ~${perSession(p)}/session
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Pricing = () => {
  return (
    <div data-testid="page-pricing">
      {/* Header */}
      <section className="pt-40 pb-16 bg-[#FDFCFB]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
            Investment
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-[#2D3A2B] leading-[1.05] tracking-tight max-w-4xl">
            An investment in your <span className="italic">long-term</span>{" "}
            wellbeing.
          </h1>
          <p className="mt-8 text-lg text-[#4A5D46] leading-relaxed max-w-3xl font-light">
            Packages are offered at discounted rates to honour long-term
            commitment to the therapeutic process. Choose the cadence and depth
            that feels right for you.
          </p>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-16 md:py-20 bg-[#F4F1ED]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-8">
          <Tier
            title="60-Minute Sessions"
            subtitle="Focused, precise therapeutic sessions."
            single={150}
            packages={sixty}
            accent="#E4D5C9"
            testidPrefix="sixty"
          />
          <Tier
            title="90-Minute Sessions"
            subtitle="Expanded depth for integration & practice."
            single={165}
            packages={ninety}
            accent="#D3DBC5"
            testidPrefix="ninety"
          />
        </div>
      </section>

      {/* Payment Notes */}
      <section className="py-24 md:py-32 bg-[#FDFCFB]">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="text-xs tracking-[0.35em] uppercase text-[#7B8E76] mb-6">
            Payment Terms
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-[#2D3A2B] leading-tight mb-8">
            Simple, secure, and transparent.
          </h2>
          <div className="space-y-4 text-[#4A5D46] font-light leading-relaxed">
            <p>
              Invoices are securely processed via <strong>Wave</strong>.
              Payments are accepted via E-transfer, Cash, or Credit Card.
            </p>
            <p className="text-sm text-[#7B8E76] italic">
              Please note: a 2% convenience fee applies to all credit card
              transactions.
            </p>
          </div>
          <div className="mt-12">
            <Link to="/contact" data-testid="pricing-cta-book">
              <Button className="rounded-full bg-[#8A9A83] hover:bg-[#7B8E76] text-white px-8 py-6 h-auto">
                Book a Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
